import React, { createContext, useCallback, useContext, useState } from 'react';
import AutoDismissMessage from './AutoDismissMessage';
import ConfirmModal from './ConfirmModal';

type ConfirmOptions = {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
};

type MessageOptions = {
  duration?: number;
  type?: 'success' | 'error' | 'warning' | 'info';
  title?: string;
};

type UIContextType = {
  showMessage: (message: string, options?: MessageOptions) => void;
  askConfirm: (opts?: ConfirmOptions) => Promise<boolean>;
};

const UIContext = createContext<UIContextType | undefined>(undefined);

export const useUI = (): UIContextType => {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used within UIMessageProvider');
  return ctx;
};

const UIMessageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messageState, setMessageState] = useState<{
    message: string;
    visible: boolean;
    duration: number;
    type: 'success' | 'error' | 'warning' | 'info';
    title?: string;
  }>({
    message: '',
    visible: false,
    duration: 3000,
    type: 'success'
  });

  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    opts: ConfirmOptions;
    resolver?: (v: boolean) => void;
  }>({ open: false, opts: {} });

  const showMessage = useCallback((msg: string, options: MessageOptions = {}) => {
    setMessageState({
      message: msg,
      visible: true,
      duration: options.duration || 3000,
      type: options.type || 'success',
      title: options.title
    });
  }, []);

  const askConfirm = useCallback((opts: ConfirmOptions = {}) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ open: true, opts, resolver: resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirmState.resolver) confirmState.resolver(true);
    setConfirmState({ open: false, opts: {} });
  }, [confirmState]);

  const handleCancel = useCallback(() => {
    if (confirmState.resolver) confirmState.resolver(false);
    setConfirmState({ open: false, opts: {} });
  }, [confirmState]);

  const handleMessageClose = useCallback(() => {
    setMessageState(prev => ({ ...prev, visible: false }));
  }, []);

  return (
    <UIContext.Provider value={{ showMessage, askConfirm }}>
      {children}

      <AutoDismissMessage 
        show={messageState.visible}
        message={messageState.message}
        duration={messageState.duration}
        type={messageState.type}
        title={messageState.title}
        onClose={handleMessageClose}
      />

      <ConfirmModal
        isOpen={confirmState.open}
        title={confirmState.opts.title}
        message={confirmState.opts.message}
        confirmText={confirmState.opts.confirmText}
        cancelText={confirmState.opts.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </UIContext.Provider>
  );
};

export default UIMessageProvider;